import {createElement, useCallback, useEffect} from 'react';
import {useParams} from 'react-router-dom';

import isEmpty from 'lodash.isempty';
import {PushNotificationBeforeReviewTab} from 'src/components/push-notifications/push-notification-before-review-tab';
import {PushNotificationExistingEvent} from 'src/components/push-notifications/push-notification-existing-event';
import {useUserEmail} from 'src/hooks/useUserEmail';
import {Params} from 'src/modules/event-create/event-create.controller';
import {EventBasicInfo} from 'src/openapi/models/EventBasicInfo';

import {Icons} from '@pb/kit-component-icon';
import {usePushNotification} from '@pb/kit-component-push-notification';

import {EventCreationMode, HeaderFields, PushNotificationIds} from '../../../../constants';
import {createEventCreationRoutePath} from '../../../../event-creation-route-planner';
import {StringFilterItem} from '../typedefs';
import {findEditChangeRequest} from '../utils/findEditChangeRequest';
import {findNotEditChangeRequest} from '../utils/findNotEditChangeRequest';

type UseEventCodeMatchParams = {
    eventsInfo: EventBasicInfo[],
    eventsInfoMapped: StringFilterItem[],
    changeRequestsMapped: StringFilterItem[],
    code: string,
    source: string,
    changeRequestId?: number | null,
    onChange: (value: string, name?: string) => void,
};

export const useEventCodeMatch = ({
    eventsInfo,
    eventsInfoMapped,
    changeRequestsMapped,
    code,
    source,
    changeRequestId,
    onChange,
}: UseEventCodeMatchParams) => {
    const userEmail = useUserEmail();
    const eventId = parseInt(useParams<Params>()?.eventId || '');
    const changeRequestUpdateId = parseInt(useParams<Params>()?.changeRequestId || '');
    const currentChangeRequestId = changeRequestId || changeRequestUpdateId || 0;
    const {onCreateNote} = usePushNotification();

    const checkMatchesEvent = useCallback((nextCode = code, nextSource = source) => {
        const matchEvent = eventsInfoMapped.find(event =>
            event.name === nextCode && event.source === nextSource,
        );

        const matchNotEditChangeRequest = findNotEditChangeRequest({
            changeRequestsMapped,
            code: nextCode,
            source: nextSource,
            userEmail,
        });

        const matchEditChangeRequest = findEditChangeRequest({
            changeRequestsMapped,
            code: nextCode,
            source: nextSource,
            userEmail,
        });

        const matchIntersection = eventsInfo.find(event =>
            event.code === matchNotEditChangeRequest?.name &&
            event.source === matchNotEditChangeRequest?.source,
        );

        const matchCreationEventId = matchEditChangeRequest?.eventId || matchEvent?.eventId || 0;
        const matchCreationChangeRequestId = matchEditChangeRequest?.changeRequestId || 0;
        const matchReviewChangeRequestId = matchNotEditChangeRequest?.changeRequestId || 0;
        const isExistOpenedChangeRequest = matchNotEditChangeRequest || matchIntersection;

        if (isExistOpenedChangeRequest) {
            onChange('', HeaderFields.eventSource);
            onCreateNote({
                id: PushNotificationIds.changeRequestAnotherAuthor,
                body: () => createElement(PushNotificationBeforeReviewTab, {
                    code: nextCode,
                    source: nextSource,
                    changeRequestId: matchReviewChangeRequestId,
                }),
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: 10000,
            });

            return;
        }

        const isOwnChangeRequest = !!matchEditChangeRequest &&
            matchEditChangeRequest.changeRequestId === currentChangeRequestId;

        if (isOwnChangeRequest) {
            return;
        }

        const isDifferentChangeRequest = matchCreationChangeRequestId !== currentChangeRequestId;
        const isDifferentEvent = !!matchEvent && matchEvent.eventId !== eventId;
        const shouldShowExistingEventNotification =
            matchEditChangeRequest ? isDifferentChangeRequest : isDifferentEvent;

        if (shouldShowExistingEventNotification) {
            const resolvedMode = matchEvent?.eventId ? EventCreationMode.update : EventCreationMode.create;
            const eventCreationRoutePath = createEventCreationRoutePath({
                mode: resolvedMode,
                eventId: matchCreationEventId,
                changeRequestId: matchCreationChangeRequestId,
            });

            onCreateNote({
                id: PushNotificationIds.existingEventCode,
                body: () => createElement(PushNotificationExistingEvent, {
                    code: nextCode,
                    source: nextSource,
                    navigationPath: eventCreationRoutePath,
                }),
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: 10000,
            });
        }
    }, [
        changeRequestsMapped,
        currentChangeRequestId,
        code,
        eventId,
        eventsInfo,
        eventsInfoMapped,
        onChange,
        onCreateNote,
        source,
        userEmail,
    ]);

    useEffect(() => {
        if (source && code && !isEmpty(eventsInfo)) {
            checkMatchesEvent(code, source);
        }
    }, [source, code, eventsInfo, checkMatchesEvent]);

    return {checkMatchesEvent};
};
